import React, { useState } from 'react';
import { DataService } from '../../services/dataService';
import type { IMonthItem } from '../../models/IMonthItem';
import { useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  Dropdown,
  Option,
  makeStyles,
  tokens,
  type DropdownProps,
  Tree,
  TreeItem,
  TreeItemLayout,
  Button,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover,
  DrawerHeaderTitle,
  Drawer,
  DrawerBody,
  Toolbar,
  ToolbarButton,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@fluentui/react-components';
import type { IDataItem } from '../../models/IDataItem';
import {
  Calendar20Regular,
  Delete20Regular,
  Dismiss24Regular,
  MoreHorizontal20Regular,
} from '@fluentui/react-icons';
import GenerateMonth from './generateMonth';
import EditMonth from './editMonth';

const useStyles = makeStyles({
  container: {
    padding: `0 ${tokens.spacingHorizontalL}`,
    height: '100%',
    overflow: 'auto',
  },
  treeContainer: {
    marginTop: '16px',
  },
  headerRow: {
    display: 'grid',
    width: '100%',
    gridTemplateColumns: '150px 120px 120px 140px 120px 120px 140px',
    gap: '8px',
    fontWeight: '600',
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
    padding: '8px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    marginBottom: '4px',
  },
  dataRow: {
    display: 'grid',
    gridTemplateColumns: '150px 120px 120px 140px 120px 120px 140px',
    gap: '8px',
    fontSize: '14px',
    padding: '4px 0',
  },
  cellValue: {
    color: tokens.colorNeutralForeground1,
  },
  toolbarButton: {
    backgroundColor: 'white',
    color: 'black',
    ':hover': {
      backgroundColor: '#f0f0f0',
      color: 'black',
    },
  },
});

const ROLE_STORAGE_KEY = 'userPersonalRole';
const PERSONAL_PARTS_STORAGE_KEY = 'userPersonalParts';

const getCurrentRole = (): string[] => {
  try {
    const raw = sessionStorage.getItem(ROLE_STORAGE_KEY);
    console.log('DetailView getCurrentRole - raw sessionStorage:', raw);
    if (!raw) return [];

    const parsed = JSON.parse(raw) as string[];

    if (Array.isArray(parsed)) {
      return parsed.map((r) => r.trim());
    }

    return [];
  } catch (error) {
    console.log('DetailView Error parsing role:', error);
    return [];
  }
};

const getPersonalParts = (): string[] => {
  try {
    const raw = sessionStorage.getItem(PERSONAL_PARTS_STORAGE_KEY);
    console.log('DetailView getPersonalParts - raw sessionStorage:', raw);
    if (!raw) return [];

    const parsed = JSON.parse(raw) as string[];

    if (Array.isArray(parsed)) {
      return parsed.map((r) => r.trim());
    }

    return [];
  } catch (error) {
    console.log('DetailView Error parsing personal parts:', error);
    return [];
  }
};

const Timetracking: React.FC = () => {
  const styles = useStyles();
  const location = useLocation();
  const { instance, accounts } = useMsal();
  const accountInfo = accounts[0] || null;
  const dataService = React.useMemo(() => {
    return accountInfo ? new DataService(instance, accountInfo) : null;
  }, [instance, accountInfo]);
  const [roles, setRoles] = useState<string[]>(getCurrentRole());
  const [personalParts, setPersonalParts] =
    useState<string[]>(getPersonalParts());
  const [monthItems, setMonthItems] = useState<IMonthItem[]>([]);
  const [jahre, setJahre] = useState([] as IDataItem[]);
  const [auswahlJahre, setAuswahlJahre] = useState([] as string[]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedMonth, setSelectedMonth] = useState<IMonthItem | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);

  React.useEffect(() => {
    const handleRoleChange = () => {
      const newRoles = getCurrentRole();
      setRoles(newRoles);
      const newPersonalParts = getPersonalParts();
      setPersonalParts(newPersonalParts);
    };

    window.addEventListener('role-changed', handleRoleChange);
    window.addEventListener('storage', handleRoleChange);

    return () => {
      window.removeEventListener('role-changed', handleRoleChange);
      window.removeEventListener('storage', handleRoleChange);
    };
  }, [roles]);

  const isWriter =
    roles.some(
      (r) =>
        r.toLowerCase() === 'Admin'.toLowerCase() ||
        r.toLowerCase() === 'Personal'.toLowerCase()
    ) ||
    personalParts.some(
      (p) => p.toLowerCase() === 'timetracking_write'.toLowerCase()
    );

  React.useEffect(() => {
    const fetchFilterOptions = async (): Promise<void> => {
      const year = location.state.meldedatum
        ? new Date(location.state.meldedatum).getFullYear()
        : new Date().getFullYear();
      for (let i = new Date().getFullYear(); i >= year; i--) {
        setJahre((prev) => [
          ...prev,
          { value: i.toString(), text: i.toString() },
        ]);
      }
    };
    fetchFilterOptions();
  }, [location.state.personalGuid, location.state.meldedatum]);

  React.useEffect(() => {
    const fetchItems = async () => {
      if (!dataService) return;
      if (!location.state?.personalGuid) return;
      const response = await dataService.getTimetrackingPersonalMonth(
        location.state?.personalGuid,
        auswahlJahre
      );
      if (response) setMonthItems(response);
    };
    fetchItems();
  }, [
    dataService,
    location.state.personalGuid,
    auswahlJahre,
    isDrawerOpen,
    isEditDrawerOpen,
  ]);

  // Group months by year
  const groupedByYear = React.useMemo(() => {
    const grouped = new Map<number, IMonthItem[]>();
    monthItems.forEach((item) => {
      const year = item.jahr;
      if (!grouped.has(year)) {
        grouped.set(year, []);
      }
      grouped.get(year)?.push(item);
    });
    // Sort years descending
    return new Map([...grouped.entries()].sort((a, b) => b[0] - a[0]));
  }, [monthItems]);

  const onJahreSelect = React.useCallback<
    NonNullable<DropdownProps['onOptionSelect']>
  >((_, data) => {
    setAuswahlJahre(data.selectedOptions);
  }, []);

  const handleShowMonth = (month: IMonthItem) => {
    const date = new Date();
    date.setFullYear(month.jahr, month.monat - 1, 1);
    setSelectedDate(date);
    setSelectedMonth(month);
    setIsDrawerOpen(true);
  };

  const handleEditMonth = (month: IMonthItem) => {
    setSelectedMonth(month);
    setSelectedDate(new Date(month.jahr, month.monat - 1, 1));
    setIsEditDrawerOpen(true);
  };

  const handleDeleteMonth = async (month: IMonthItem) => {
    if (!dataService) return;
    if (!location.state?.personalGuid) return;
    await dataService.deleteMonthItem(month.itemId);
    const response = await dataService.getTimetrackingPersonalMonth(
      location.state?.personalGuid,
      auswahlJahre
    );
    if (response) setMonthItems(response);
  };

  const handleDrawerClose = async () => {
    setIsEditDrawerOpen(false);
    setIsDrawerOpen(false);
  };

  const handleAddMonth = () => {
    setSelectedMonth(null);
    setSelectedDate(new Date());
    setIsDrawerOpen(true);
  };

  return (
    <>
      {isWriter && (
        <Toolbar aria-label="Add month">
          <ToolbarButton
            aria-label="add month"
            appearance="primary"
            icon={<Calendar20Regular />}
            onClick={handleAddMonth}
            className={styles.toolbarButton}
          >
            Monat hinzufügen
          </ToolbarButton>
        </Toolbar>
      )}
      <div className={styles.container}>
        <div className="dropdownContainer" style={{ marginTop: '8px' }}>
          <Dropdown
            title="Filtern nach Jahren"
            multiselect={true}
            selectedOptions={auswahlJahre}
            value={
              auswahlJahre.length > 0
                ? auswahlJahre
                    .map((id) => jahre.find((t) => t.value === id)?.text || id)
                    .join(', ')
                : ''
            }
            onOptionSelect={onJahreSelect}
            style={{ marginLeft: '10px', width: '250px' }}
          >
            {jahre.map((t) => (
              <Option key={t.value} value={t.value}>
                {t.text}
              </Option>
            ))}
          </Dropdown>
        </div>
        {monthItems.length > 0 ? (
          <div className={styles.treeContainer}>
            <Tree
              aria-label="Timetracking"
              defaultOpenItems={
                groupedByYear.size > 0
                  ? [Array.from(groupedByYear.keys())[0].toString()]
                  : []
              }
            >
              {Array.from(groupedByYear.entries()).map(([year, months]) => (
                <TreeItem key={year} value={year.toString()} itemType="branch">
                  <TreeItemLayout>{year}</TreeItemLayout>
                  <Tree>
                    <TreeItem
                      key={`${year}-header`}
                      value={`${year}-header`}
                      itemType="leaf"
                    >
                      <TreeItemLayout>
                        <div className={styles.headerRow}>
                          <div>Monat</div>
                          <div>Sollstunden</div>
                          <div>Gesamtstunden</div>
                          <div>Monatsüberschuss</div>
                          <div>Überstunden</div>
                          <div>Urlaubsrest</div>
                          <div>Workflow-Status</div>
                        </div>
                      </TreeItemLayout>
                    </TreeItem>
                    {months.map((month) => (
                      <TreeItem key={`${year}-${month.monat}`} itemType="leaf">
                        <TreeItemLayout
                          actions={
                            <>
                              {isWriter && (
                                <Dialog>
                                  <DialogTrigger disableButtonEnhancement>
                                    <Button
                                      aria-label="Remove item"
                                      appearance="subtle"
                                      icon={<Delete20Regular />}
                                    />
                                  </DialogTrigger>
                                  <DialogSurface
                                    aria-labelledby={`${month.itemId.toString()}-title`}
                                    aria-describedby={`${month.itemId.toString()}-content`}
                                  >
                                    <DialogBody>
                                      <DialogTitle
                                        id={`${month.itemId.toString()}-title`}
                                      >
                                        Monat löschen
                                      </DialogTitle>
                                      <DialogContent
                                        id={`${month.itemId.toString()}-content`}
                                      >
                                        Wollen Sie den Monat {month.strMonat}{' '}
                                        wirklich löschen?
                                      </DialogContent>
                                      <DialogActions>
                                        <DialogTrigger disableButtonEnhancement>
                                          <Button
                                            appearance="primary"
                                            onClick={() =>
                                              handleDeleteMonth(month)
                                            }
                                          >
                                            Monat löschen
                                          </Button>
                                        </DialogTrigger>
                                        <DialogTrigger disableButtonEnhancement>
                                          <Button appearance="secondary">
                                            Abbrechen
                                          </Button>
                                        </DialogTrigger>
                                      </DialogActions>
                                    </DialogBody>
                                  </DialogSurface>
                                </Dialog>
                              )}
                              <Menu>
                                <MenuTrigger disableButtonEnhancement>
                                  <Button
                                    aria-label="Options"
                                    appearance="subtle"
                                    icon={<MoreHorizontal20Regular />}
                                  />
                                </MenuTrigger>
                                <MenuPopover>
                                  <MenuList>
                                    <MenuItem
                                      onClick={() => handleShowMonth(month)}
                                    >
                                      Show
                                    </MenuItem>
                                    {isWriter && (
                                      <MenuItem
                                        onClick={() => handleEditMonth(month)}
                                      >
                                        Edit
                                      </MenuItem>
                                    )}
                                  </MenuList>
                                </MenuPopover>
                              </Menu>
                            </>
                          }
                        >
                          <div
                            className={styles.dataRow}
                            onDoubleClick={() => handleShowMonth(month)}
                          >
                            <div className={styles.cellValue}>
                              {month.strMonat}
                            </div>
                            <div className={styles.cellValue}>
                              {month.strSollStunden || ''}
                            </div>
                            <div className={styles.cellValue}>
                              {month.strGesamtStunden || ''}
                            </div>
                            <div className={styles.cellValue}>
                              {month.strMonatsUeberschuss || ''}
                            </div>
                            <div className={styles.cellValue}>
                              {month.strUeberStunden || ''}
                            </div>
                            <div className={styles.cellValue}>
                              {month.urlaubsRest ?? ''}
                            </div>
                            <div className={styles.cellValue}>
                              {month.workflowStatus || ''}
                            </div>
                          </div>
                        </TreeItemLayout>
                      </TreeItem>
                    ))}
                  </Tree>
                </TreeItem>
              ))}
            </Tree>
          </div>
        ) : (
          <p
            style={{
              fontSize: '16px',
              marginBottom: '30px',
              maxWidth: '500px',
            }}
          >
            Keine Monate gefunden.
          </p>
        )}
      </div>
      <Drawer
        size="full"
        open={isDrawerOpen}
        onOpenChange={(_, { open }) => {
          if (!open) {
            handleDrawerClose();
          } else {
            setIsDrawerOpen(true);
          }
        }}
        position="end"
      >
        <DrawerHeaderTitle
          style={{ paddingLeft: '15px' }}
          action={
            <Button
              appearance="subtle"
              aria-label="Close"
              icon={<Dismiss24Regular />}
              onClick={() => handleDrawerClose()}
            />
          }
        >
          {selectedMonth?.strMonat !== undefined
            ? `${selectedMonth.strMonat} - ${selectedMonth.name}`
            : 'Select month'}
        </DrawerHeaderTitle>
        <DrawerBody>
          <GenerateMonth
            mitarbeiterGuid={location.state.personalGuid}
            selectedMonth={selectedMonth}
            selectedDate={selectedDate}
            onClose={handleDrawerClose}
          />
        </DrawerBody>
      </Drawer>
      <Drawer
        size="medium"
        open={isEditDrawerOpen}
        onOpenChange={(_, { open }) => {
          if (!open) {
            handleDrawerClose();
          } else {
            setIsEditDrawerOpen(true);
          }
        }}
        position="end"
      >
        <DrawerHeaderTitle
          style={{ paddingLeft: '15px' }}
          action={
            <Button
              appearance="subtle"
              aria-label="Close"
              icon={<Dismiss24Regular />}
              onClick={() => handleDrawerClose()}
            />
          }
        >
          {selectedMonth?.strMonat !== undefined
            ? `${selectedMonth.strMonat}`
            : 'No month selected'}
        </DrawerHeaderTitle>
        <DrawerBody>
          <EditMonth
            selectedMonth={selectedMonth}
            onClose={handleDrawerClose}
          />
        </DrawerBody>
      </Drawer>
    </>
  );
};

export default Timetracking;
